<template>
  <s-modal
    v-model="showModal"
    class="recognize-info-modal"
    data-test="recognize-modal"
    size="lg"
    :ok-confirm-message-code="okConfirmMessageCode"
    :confirm-type="confirmationType"
    :ok-only="!alarm.offlineAlarm"
    :ok-title="okTitle"
    :is-loading="loading"
    @cancel="close"
    @ok="ignoreAlarm"
    @toggle-message="toggleHeader"
  >
    <template #modal-header v-if="showHeader">
      <alarm-header-modal
        :title="$t('alarms-and-warnings.recognition.recognition-information')"
        :company="alarm.companyName"
        :regional="alarm.regionalName"
        :installation="alarm.installationName"
        :asset="alarm.assetName"
      />
      <button
        class="close"
        type="button"
        aria-label="Close"
        @click="close"
      >
        &times;
      </button>
    </template>
    <strong class="recognize-info-modal__alarm-data-title">
      {{ $t('alarms-and-warnings.recognition.alarm-data') }}:
    </strong>
    <recognize-alarm-data
      class="border p-2 rounded mb-3"
      :alarm="alarm"
    />
    <strong class="recognize-info-modal__recognize-data-title">
      {{ $t('alarms-and-warnings.recognition.recognition-data') }}:
    </strong>
    <div class="border p-2 rounded">
      <div class="recognize-info-modal__info-container">
        <span class="recognize-info-modal__title">
          {{ $t('alarms-and-warnings.recognition.user') }}:
        </span>
        {{ alarm.recognizedUserName }}
      </div>
      <div class="recognize-info-modal__info-container">
        <span class="recognize-info-modal__title">
          {{ $t('alarms-and-warnings.recognition.recognition-date-time') }}:
        </span>
        <date-time-label
          :value="alarm.recognizedDateAndTime"
          :format="dateLabelFormat"
        />
      </div>
      <div class="recognize-info-modal__info-container">
        <span class="recognize-info-modal__title">
          {{ $t('alarms-and-warnings.recognition.comment') }}:
        </span>
        <b-form-textarea
          :rows="3"
          :max-rows="6"
          maxlength="400"
          :disabled="!alarm.offlineAlarm"
          v-model="alarm.recognizedComment"
        />
        <div
          v-if="alarm.offlineAlarm"
          class="d-flex justify-content-end mt-2"
          data-test="offline-checkbox-wrapper"
        >
          <b-checkbox
            v-model="ignore"
            disabled
            data-test="offline-checkbox"
          >
            {{ $t('alarms-and-warnings.ignore-event') }}
          </b-checkbox>
          <info-badge
            data-test="offline-checkbox-badge"
            class="ml-2"
            translation-key="alarms-and-warnings.ignore-event-tooltip"
            placement="top"
          />
        </div>
      </div>
    </div>
  </s-modal>
</template>

<script>
import WaitManager from '@/services/waitManager';
import MonitoringService from '@/domain/services/generalPanel/dashboard';
import InfoBadge from '@/components/icon/InfoBadge';
import ConfirmationTypes from '@/constants/confirmationTypes';
import { formats } from '@/constants/localizer';
import AlarmHeaderModal from '@/domain/components/generalPanel/alarmsAndWarnings/recognizeAlarmModal/AlarmHeaderModal';
import RecognizeAlarmData from './AlarmData';

export default {
  props: {
    alarm: {
      type: Object,
      required: true,
    },
  },
  components: {
    AlarmHeaderModal,
    RecognizeAlarmData,
    InfoBadge,
  },
  data: () => ({
    waitManager: null,
    ignore: true,
    showModal: false,
    showHeader: true,
    loading: false,
    confirmationType: ConfirmationTypes.YES_NO,
  }),
  computed: {
    okTitle() {
      return this.alarm.offlineAlarm ? this.$t('general.confirm') : this.$t('general.ok');
    },
    okConfirmMessageCode() {
      return this.alarm.offlineAlarm ? 'MC041' : null;
    },
    dateLabelFormat() {
      return formats.DATETIME_SHORT_WITH_SECONDS;
    },
  },
  methods: {
    toggleHeader() {
      this.showHeader = !this.showHeader;
    },
    show() {
      this.showModal = true;
      this.showHeader = true;
      this.waitManager = new WaitManager();
      return this.waitManager.waiter;
    },
    close() {
      this.waitManager.reject();
      this.showModal = false;
    },
    ignoreAlarm() {
      if (!this.alarm.offlineAlarm) {
        this.showModal = false;
        return this.waitManager.accept();
      }

      this.loading = true;

      return MonitoringService.ignoreAlarm({
        alarmId: this.alarm.id,
        description: this.alarm.recognizedComment,
      })
      .then(() => {
        this.$pushAlert({ code: 'MS027' });
        this.$emit('ignored-alarm');
        this.waitManager.accept();
      })
      .catch(() => {
        this.waitManager.reject();
      })
      .finally(() => {
        this.loading = false;
        this.showModal = false;
      });
    },
  },
};
</script>

<style lang="sass" scoped>
  .recognize-info-modal
    &__title
      font-weight: bold
    &__alarm-data-title
      color: var(--color-primary)
    &__recognize-data-title
      color: var(--color-secondary)
    &__info-container
      margin-bottom: 5px
      &_horizontal
        display: flex
        flex-direction: column
</style>
